// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

// Utils
import Module from "./module";
import i18n, { t, te } from "@/plugins/i18n";
import Route from "@/router/Route";
import { useBaseStore } from "@/stores/base";

// Modules
import home from "./home/index";
import environment from "./environment/index";
import roles from "./roles/index";
import dashboard from "@/modules/dashboards/index";
import notifications from "./notifications/index";
import router from "@/router/router";
import users from "./users/index";
import profile from "./profile/index";
import explorer from "./explorer/index";
import exports from "./exports/index";
import annotations from "@/modules/annotations";
import capacity from "@/modules/capacity_editor/index";
import hierarchy from "@/modules/hierarchy";
import traits from "./dimension_values";
import alerts from "./alerts";
import segments from "./segments";
import scheduler from "./scheduler";
import pricelists from "./booking_experts";
import testing from "./testing";
import ruleEngine from "./rule_engine";
import integrations from "./integrations";
import pro_loyalty from "./pro_loyalty";
import targets from "./targets";
import CampaignPlanner from "./campaign_planner";
import pqa from "./pqa";
import basePrice from "./base_price";
import { setTranslator } from "chartjs-graph";
import BaseLayout from "@/ui/layouts/BaseLayout.vue";

// -----------------------------------------------------------------------------

/** All the given modules that should be loaded. */
export const modules: Module[] = [
	home,
	dashboard,
	roles,
	environment,
	notifications,
	users,
	profile,
	explorer,
	exports,
	annotations,
	capacity,
	hierarchy,
	traits,
	alerts,
	segments,
	scheduler,
	pricelists,
	testing,
	ruleEngine,
	integrations,
	pro_loyalty,
	targets,
	CampaignPlanner,
	pqa,
	basePrice,
];

// -----------------------------------------------------------------------------

/**
 * Inherit parent permissions to child routes recursively.
 * @param permissions The permissions to inherit.
 * @param routes The routes to inherit the permissions to.
 * @returns The routes with inherited permissions.
 */
function inheritParentPermissions(
	permissions: string[],
	routes: Route[]
): Route[] {
	return routes.map((route) => {
		route.meta.permissions = Array.from(
			new Set([...(route.meta.permissions ?? []), ...permissions])
		);

		if (Array.isArray(route.children)) {
			route.children = inheritParentPermissions(permissions, route.children);
		}

		return route;
	});
}

/**
 * Appends a route and its children to the router. with the given permissions.
 * @param route The route to add.
 * @param modulePermission The permissions of the module.
 */
function addRoutes(route: Route, modulePermission: string[] = []) {
	const routeNameSegments = route.name.split(".");
	const parentRouteName = routeNameSegments.slice(0, -1).join(".");
	const concatenatedPermissions = [
		...(route.meta.permissions ?? []),
		...modulePermission,
	];

	if (concatenatedPermissions.length > 0) {
		route.children = inheritParentPermissions(
			concatenatedPermissions,
			route.children
		);
	}

	router.addRoute("baseForLoggedIn", route);
}

/**
 * Adds translations for the given module.
 * @param module The module to add translations for.
 */
function addTranslations(module: Module) {
	Object.keys(module.translations).forEach((locale: string) => {
		i18n.global.mergeLocaleMessage(locale, module.translations[locale]);
	});
}

// -----------------------------------------------------------------------------

/** Registers all routes and translations for the modules. */
export async function registerRoutesAndTranslations() {
	setTranslator(t, te);

	router.addRoute({
		name: "baseForLoggedIn",
		path: "",
		component: BaseLayout,
		children: [],
	});

	modules.forEach((module) => {
		addTranslations(module);
		addRoutes(module.baseRoute, module.permissions);
	});

	router.addRoute({
		name: "catch-all",
		redirect: "/404",
		path: "/:pathMatch(.*)*",
	});

	// Delay added to wait for PINA's initialisation.
	useBaseStore().moduleRoutesLoaded = true;
}
